import React, { useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import InputMask from 'react-input-mask';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Switch from '@material-ui/core/Switch';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  slider: {
    maxWidth: '95%',
  },
});

// Render each question/answer in a view only state
const ViewOnlyAnswerComponent = ({ answer }) => {
  // If the question is not admin answerable, don't render an edit field
  if (!answer?.question?.admin_can_answer) return null;

  const { question } = answer;

  // Raw value of answer
  const answerValue = answer.answer;
  // Don't return a view component if there is no value
  if (answerValue === null || answerValue === '') return null;

  // Example 'field_name' for question: "Brand"
  // Example 'questionText': "What brand of tire do you prefer?"
  const { field_name: fieldName, question: questionText } = question;

  // Slider config
  const { config_range_max_value } = question;

  let answerContent = '';
  switch (question.type) {
    case 'boolean':
      answerContent = answerValue === 'true' ? 'Yes' : 'No';
      break;
    case 'slider':
      answerContent = `${answerValue}/${config_range_max_value}`;
      break;
    default:
      answerContent = answerValue;
  }

  // Format and return answer component with label
  return (
    <Grid item xs={12}>
      <Typography>
        <strong>{fieldName || questionText}: </strong> {answerContent}
      </Typography>
    </Grid>
  );
};

const EditableAnswerComponent = ({
  index,
  answer,
  setAnswers,
  itemSize = 6,
}) => {
  const classes = useStyles();

  const { question } = answer;

  // Raw value of answer
  const answerValue = answer.answer;

  // Example 'field_name' for question: "Brand"
  const { field_name: fieldName } = question;

  // Slider config
  const {
    config_range_min_value,
    config_range_max_value,
    config_range_min_label,
    config_range_max_label,
    config_range_step,
  } = question;

  // Default validation function
  let isInvalid = (value) => false;

  // Handler to update parent state for for all input types
  const updateAnswers = (value) =>
    setAnswers((prevAnswers) => {
      const updatedAnswers = prevAnswers.concat();
      updatedAnswers[index].answer = value;
      updatedAnswers[index].invalid = isInvalid(value);
      return updatedAnswers;
    });

  let answerComponent;
  switch (question.type) {
    case 'long_text':
    case 'short_text':
      answerComponent = (
        <TextField
          multiline={question.type === 'long_text'}
          value={answerValue}
          onChange={(event) => {
            updateAnswers(event.target.value);
          }}
        />
      );
      break;
    case 'tire_size':
      isInvalid = (value) =>
        value && value.includes('_')
          ? 'Tire size must contain 10 characters'
          : false;
      answerComponent = (
        <InputMask
          value={answerValue}
          mask="P999/99R99"
          onChange={(event) =>
            updateAnswers(
              event.target.value === 'P___/__R__' ? null : event.target.value,
            )
          }
        >
          {() => <TextField placeholder="P___/__R__" />}
        </InputMask>
      );
      break;
    case 'boolean':
      answerComponent = (
        <Switch
          checked={answerValue === 'true'}
          onChange={(event) =>
            updateAnswers(event.target.checked ? 'true' : 'false')
          }
        />
      );
      break;
    case 'slider':
      answerComponent = (
        <Slider
          className={classes.slider}
          defaultValue={Number(answerValue)}
          min={config_range_min_value}
          max={config_range_max_value}
          step={config_range_step}
          marks={[
            {
              value: config_range_min_value,
              label: config_range_min_label || config_range_min_value,
            },
            {
              value: answerValue,
              // Only display label if the current value is not max/min, so we don't have double labels
              label: ![config_range_max_value, config_range_min_value].includes(
                answerValue,
              )
                ? answerValue
                : null,
            },
            {
              value: config_range_max_value,
              label: config_range_max_label || config_range_max_value,
            },
          ]}
          valueLabelDisplay="off"
          onChange={(event, value) => {
            updateAnswers(value);
          }}
        />
      );
      break;
    case 'integer':
      isInvalid = (value) =>
        !Number.isInteger(Number(value))
          ? `Please enter a whole number for ${fieldName}`
          : false;
      answerComponent = (
        <TextField
          value={answerValue}
          onChange={(event) => {
            updateAnswers(event.target.value);
          }}
        />
      );
      break;
    case 'multichoice':
      const { config_choices } = question;
      answerComponent = (
        <Select
          value={answerValue}
          onChange={(event) => {
            updateAnswers(event.target.value);
          }}
          renderValue={(value) => <span>{value}</span>}
        >
          {config_choices.map((choice) => (
            <MenuItem value={choice}>{choice}</MenuItem>
          ))}
        </Select>
      );
    default:
      break;
  }

  const errorMessage = isInvalid(answerValue);
  return (
    <Grid item xs={itemSize} style={{ padding: '10px' }}>
      <InputLabel>{fieldName}</InputLabel>
      <FormControl error={!!errorMessage} fullWidth>
        {answerComponent}
        <FormHelperText>{errorMessage}</FormHelperText>
      </FormControl>
    </Grid>
  );
};

export const ViewOnlyQuoteAnswerList = ({ answers = [] }) => {
  return (
    <Grid container direction="column" spacing={0}>
      {answers
        .sort(
          (answer1, answer2) =>
            answer1.question.display_order - answer2.question.display_order,
        )
        .map((answer, index) => (
          <ViewOnlyAnswerComponent
            key={`viewable-answer-${index}`}
            answer={answer}
          />
        ))}
    </Grid>
  );
};

export const EditableQuoteAnswerList = ({
  answers,
  setAnswers,
  setIsValid = () => {},
  questionType = 'admin',
}) => {
  // Filter answers by question type (admin or customer)
  // First store original indexes for updating in parent state
  const filteredAnswers = answers
    .map((answer, i) => ({ ...answer, index: i }))
    .filter(({ question }) => {
      const { customer_can_answer, admin_can_answer } = question;
      if (customer_can_answer && questionType == 'customer') return true;
      if (admin_can_answer && questionType == 'admin') return true;
      return false;
    });

  // Whenever the value of an answer changes, check for overall form validity
  useEffect(() => {
    setIsValid(!filteredAnswers.some((answer) => answer.invalid));
  }, [answers]);

  const answersGroupedByOrder = filteredAnswers.reduce(
    (groups, currentAnswer) => {
      const { display_order } = currentAnswer.question;
      const group = groups[display_order] || [];
      group.push(currentAnswer);
      return { ...groups, [display_order]: group };
    },
    {},
  );

  const display_orders = [
    ...new Set(filteredAnswers.map(({ question }) => question.display_order)),
  ].sort((a, b) => a.display_order - b.display_order);

  return (
    <Grid container direction="column" style={{ padding: '15px 0' }}>
      {display_orders.map((display_order) => {
        // The size of each grid item should be determined by however many answers we want on a given row
        // However, the minimum size should be at least 2
        const itemSize = Math.max(
          2,
          Math.floor(12 / answersGroupedByOrder[display_order].length),
        );
        return (
          <Grid item container direction="row" alignItems="flex-end">
            {answersGroupedByOrder[display_order].map((answer) => (
              <EditableAnswerComponent
                key={`editable-answer-${answer.index}`}
                index={answer.index}
                answer={answer}
                setAnswers={setAnswers}
                itemSize={itemSize}
              />
            ))}
          </Grid>
        );
      })}
    </Grid>
  );
};
